import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Dns as DnsIcon,
  FlashOn as FlashOnIcon,
  Language as LanguageIcon,
  MoreVert as MoreVertIcon,
  NetworkCheck as NetworkCheckIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import api, {
  AllowListCreateRequest,
  AssetDiscoveryUpdateRequest,
  AssetEntry,
  AssetDetailResponse,
} from '../services/api';
import { ALLOW_LIST_STATUS_COLORS, ALLOW_LIST_TYPE_COLORS } from '../types/allowList';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useTenant } from '../contexts/TenantContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { PermissionGate } from '../components/PermissionGate';
import { formatUiDateOnly, formatUiDateTime } from '../utils/dateUtils';
import { providerLabel, providerChipColor } from '../utils/hostingProvider';

const discoveryChipColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'completed') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'failed') return 'error';
  return 'default';
};

type AssetSortColumn = 'value' | 'type' | 'status' | 'hosting' | 'discovered' | 'discovery' | 'created_at';
type SortDirection = 'asc' | 'desc';

const Assets: React.FC = () => {
  const { timezone } = useTimezone();
  const { selectedTenant } = useTenant();
  const { showNotification } = useNotification();
  const { hasPermission, isAdmin } = usePermissions();

  const [assets, setAssets] = useState<AssetEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetEntry | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAsset, setMenuAsset] = useState<AssetEntry | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, asset: AssetEntry) => {
    setMenuAnchor(event.currentTarget);
    setMenuAsset(asset);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuAsset(null);
  };

  const [newEntry, setNewEntry] = useState<AllowListCreateRequest>({ value: '', reason: '' });
  const [newValues, setNewValues] = useState<string[]>(['']);
  const [addingAsset, setAddingAsset] = useState(false);
  const [editPayload, setEditPayload] = useState<AssetDiscoveryUpdateRequest>({ hosting_provider: '', notes: '' });
  const [expandedDiscoveries, setExpandedDiscoveries] = useState<Record<string, boolean>>({});
  const [sortBy, setSortBy] = useState<AssetSortColumn>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);
  const [assetDetails, setAssetDetails] = useState<Record<string, AssetDetailResponse>>({});
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null);

  const navigate = useNavigate();
  const canRequestWhitelist = isAdmin || hasPermission('request_allow_list_entries');
  const canEditDiscovery = isAdmin || hasPermission('manage_allow_list');
  const showActionsColumn = canRequestWhitelist || canEditDiscovery;
  const currentTenantId = selectedTenant?.tenant_id;

  const toggleExpand = useCallback(
    async (assetId: string) => {
      if (expandedAssetId === assetId) {
        setExpandedAssetId(null);
        return;
      }
      setExpandedAssetId(assetId);
      if (assetDetails[assetId]) return;
      setLoadingDetailId(assetId);
      try {
        const detail = await api.getAssetDetail(assetId);
        setAssetDetails((prev) => ({ ...prev, [assetId]: detail }));
      } catch (error: any) {
        showNotification(error?.response?.data?.detail || 'Failed to load asset detail', 'error');
        setExpandedAssetId(null);
      } finally {
        setLoadingDetailId(null);
      }
    },
    [expandedAssetId, assetDetails, showNotification]
  );

  const loadAssets = useCallback(async () => {
    if (!currentTenantId) return;
    setLoading(true);
    try {
      const data = await api.getAssets(search || undefined);
      setAssets(data);
    } catch (error: any) {
      console.error('Failed to load assets:', error);
      showNotification(error?.response?.data?.detail || 'Failed to load assets', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentTenantId, search, showNotification]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const stats = useMemo(() => {
    const total = assets.length;
    const approved = assets.filter((a) => a.status === 'approved').length;
    const requested = assets.filter((a) => a.status === 'requested').length;
    const completedDiscovery = assets.filter((a) => a.discovery?.discovery_status === 'completed').length;
    return { total, approved, requested, completedDiscovery };
  }, [assets]);

  const getDiscoveredSortValue = useCallback((asset: AssetEntry): number => {
    const d = asset.discovery;
    if (!d || d.discovery_status !== 'completed') return -1;
    if (asset.type === 'cidr') {
      const parts = asset.value.split('/');
      const prefix = parseInt(parts[1] ?? '32', 10);
      const hostBits = 32 - prefix;
      return hostBits > 1 ? (Math.pow(2, hostBits) - 2) : Math.pow(2, hostBits);
    }
    if (asset.type === 'ip') return d.reverse_dns?.length ?? 0;
    return d.resolved_ips?.length ?? 0;
  }, []);

  const sortValueForAsset = useCallback((asset: AssetEntry, column: AssetSortColumn): string | number => {
    switch (column) {
      case 'value':
        return asset.value || '';
      case 'type':
        return asset.type || '';
      case 'status':
        return asset.status || '';
      case 'hosting':
        return providerLabel(asset.discovery?.hosting_provider);
      case 'discovered':
        return getDiscoveredSortValue(asset);
      case 'discovery':
        return asset.discovery?.discovery_status || 'pending';
      case 'created_at':
      default:
        return new Date(asset.created_at).getTime();
    }
  }, [getDiscoveredSortValue]);

  const sortedAssets = useMemo(() => {
    return [...assets]
      .map((asset, index) => ({ asset, index }))
      .sort((a, b) => {
        const left = sortValueForAsset(a.asset, sortBy);
        const right = sortValueForAsset(b.asset, sortBy);
        let result = 0;
        if (typeof left === 'number' && typeof right === 'number') {
          result = left - right;
        } else {
          result = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
        }

        if (result === 0) {
          // Stable tie-breaker to prevent row jumpiness on equal values.
          result = a.index - b.index;
        }
        return sortDirection === 'asc' ? result : -result;
      })
      .map(({ asset }) => asset);
  }, [assets, sortBy, sortDirection, sortValueForAsset]);

  const handleSort = (column: AssetSortColumn) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDirection(column === 'created_at' ? 'desc' : 'asc');
  };

  const typeIcon = (type: string) => {
    if (type === 'dns') return <DnsIcon fontSize="small" />;
    if (type === 'url') return <LanguageIcon fontSize="small" />;
    return <NetworkCheckIcon fontSize="small" />;
  };

  const handleAddAsset = async () => {
    if (!currentTenantId) {
      showNotification('Select a tenant first', 'error');
      return;
    }
    const targets = newValues.map(v => v.trim()).filter(v => v);
    if (targets.length === 0) {
      showNotification('Asset value is required', 'error');
      return;
    }
    try {
      setAddingAsset(true);
      const result = await api.bulkCreateAllowListEntries(
        currentTenantId,
        targets,
        newEntry.reason?.trim() || undefined
      );
      const parts: string[] = [];
      if (result.created.length > 0) parts.push(`${result.created.length} added`);
      if (result.skipped.length > 0) parts.push(`${result.skipped.length} already existed`);
      if (result.failed.length > 0) parts.push(`${result.failed.length} failed`);
      showNotification(parts.join(', '), result.failed.length > 0 ? 'warning' : 'success');
      setAddOpen(false);
      setNewValues(['']);
      setNewEntry({ value: '', reason: '' });
      loadAssets();
    } catch (error: any) {
      showNotification(error?.response?.data?.detail || 'Failed to request whitelist', 'error');
    } finally {
      setAddingAsset(false);
    }
  };

  const handleEditDiscoveryOpen = (asset: AssetEntry) => {
    setSelectedAsset(asset);
    setEditPayload({
      hosting_provider: asset.discovery?.hosting_provider || '',
      notes: asset.discovery?.notes || '',
    });
    setEditOpen(true);
  };

  const handleSaveDiscovery = async () => {
    if (!selectedAsset) return;
    try {
      await api.updateAssetDiscovery(selectedAsset.id, {
        hosting_provider: editPayload.hosting_provider || undefined,
        notes: editPayload.notes || undefined,
      });
      showNotification('Asset discovery details updated', 'success');
      setEditOpen(false);
      setSelectedAsset(null);
      loadAssets();
    } catch (error: any) {
      showNotification(error?.response?.data?.detail || 'Failed to update discovery', 'error');
    }
  };

  const handleRefreshDiscovery = async (asset: AssetEntry) => {
    try {
      await api.refreshAssetDiscovery(asset.id);
      showNotification('Discovery refreshed', 'success');
      loadAssets();
    } catch (error: any) {
      showNotification(error?.response?.data?.detail || 'Failed to refresh discovery', 'error');
    }
  };

  const handleDeleteOpen = (asset: AssetEntry) => {
    setSelectedAsset(asset);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAsset) return;
    try {
      await api.deleteAsset(selectedAsset.id);
      showNotification(`Asset "${selectedAsset.value}" deleted`, 'success');
      setDeleteOpen(false);
      setSelectedAsset(null);
      loadAssets();
    } catch (error: any) {
      showNotification(error?.response?.data?.detail || 'Failed to delete asset', 'error');
    }
  };

  const toggleDiscoveryExpansion = (assetId: string) => {
    setExpandedDiscoveries((prev) => ({
      ...prev,
      [assetId]: !prev[assetId],
    }));
  };

  return (
    <PermissionGate permission="view_assets" fallback={<Typography>You do not have permission to view Assets.</Typography>}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5">Assets</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              View tenant assets, whitelist status, and initial discovery metadata.
            </Typography>
          </Box>
          {showActionsColumn && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add Asset
            </Button>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent><Typography color="text.secondary">Total Assets</Typography><Typography variant="h4">{stats.total}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent><Typography color="text.secondary">Approved</Typography><Typography variant="h4" color="success.main">{stats.approved}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent><Typography color="text.secondary">Requested</Typography><Typography variant="h4" color="warning.main">{stats.requested}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent><Typography color="text.secondary">Discovery Ready</Typography><Typography variant="h4" color="info.main">{stats.completedDiscovery}</Typography></CardContent></Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by asset value, type, or provider..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        <Paper>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48, p: 0 }} />
                  <TableCell sortDirection={sortBy === 'value' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'value'}
                      direction={sortBy === 'value' ? sortDirection : 'asc'}
                      onClick={() => handleSort('value')}
                    >
                      Asset
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'type' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'type'}
                      direction={sortBy === 'type' ? sortDirection : 'asc'}
                      onClick={() => handleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'status' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Whitelist
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'hosting' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'hosting'}
                      direction={sortBy === 'hosting' ? sortDirection : 'asc'}
                      onClick={() => handleSort('hosting')}
                    >
                      Hosting
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'discovered' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'discovered'}
                      direction={sortBy === 'discovered' ? sortDirection : 'asc'}
                      onClick={() => handleSort('discovered')}
                    >
                      Discovered
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'discovery' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'discovery'}
                      direction={sortBy === 'discovery' ? sortDirection : 'asc'}
                      onClick={() => handleSort('discovery')}
                    >
                      Discovery
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'created_at' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'created_at'}
                      direction={sortBy === 'created_at' ? sortDirection : 'asc'}
                      onClick={() => handleSort('created_at')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  {showActionsColumn && (
                    <TableCell
                      sx={{
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        width: 56,
                        boxShadow: '-4px 0 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAssets.map((asset) => (
                  <React.Fragment key={asset.id}>
                    <TableRow>
                      <TableCell sx={{ width: 48, p: 0, verticalAlign: 'middle' }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(asset.id)}
                          aria-label={expandedAssetId === asset.id ? 'Collapse' : 'Expand'}
                        >
                          {expandedAssetId === asset.id ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 340 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {typeIcon(asset.type)}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 300,
                          }}
                          title={asset.value}
                        >
                          {asset.value}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={asset.type.toUpperCase()}
                        color={ALLOW_LIST_TYPE_COLORS[asset.type as keyof typeof ALLOW_LIST_TYPE_COLORS] as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={asset.status}
                        color={ALLOW_LIST_STATUS_COLORS[asset.status as keyof typeof ALLOW_LIST_STATUS_COLORS] as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Chip
                          label={providerLabel(asset.discovery?.hosting_provider)}
                          color={providerChipColor(asset.discovery?.hosting_provider)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      {(() => {
                        const d = asset.discovery;
                        const dim = { variant: 'caption' as const, color: 'text.disabled' };
                        const provider = (d?.hosting_provider || '').toLowerCase();
                        const isOnPrem = provider === 'on_prem' || provider === 'unknown_or_on_prem';
                        const asnInfoLine = isOnPrem && d?.asn_info?.asn ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            ASN {d.asn_info.asn}{(d.asn_info.org || d.asn_info.description) ? ` - ${d.asn_info.org || d.asn_info.description}` : ''}
                          </Typography>
                        ) : null;

                        if (!d || d.discovery_status !== 'completed') {
                          return <Typography {...dim}>—</Typography>;
                        }

                        // CIDR → show host count + ASN org if known
                        if (asset.type === 'cidr') {
                          const parts = asset.value.split('/');
                          const prefix = parseInt(parts[1] ?? '32', 10);
                          const hostBits = 32 - prefix;
                          const hostCount = hostBits > 1 ? (Math.pow(2, hostBits) - 2) : Math.pow(2, hostBits);
                          const org = (d.asn_info as any)?.org || (d.asn_info as any)?.isp;
                          return (
                            <Box>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                /{prefix} · {hostCount.toLocaleString()} hosts
                              </Typography>
                              {org && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {org.length > 30 ? `${org.slice(0, 30)}…` : org}
                                </Typography>
                              )}
                              {asnInfoLine}
                            </Box>
                          );
                        }

                        // IP → show rDNS hostname(s)
                        if (asset.type === 'ip') {
                          const names = d.reverse_dns ?? [];
                          if (!names.length) {
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                <Typography {...dim}>no rDNS</Typography>
                                {asnInfoLine}
                              </Box>
                            );
                          }
                          const isExpanded = !!expandedDiscoveries[asset.id];
                          const visibleNames = isExpanded ? names : names.slice(0, 2);
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {visibleNames.map((n) => (
                                <Typography key={n} variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                  {n}
                                </Typography>
                              ))}
                              {names.length > 2 && (
                                <Button
                                  size="small"
                                  onClick={() => toggleDiscoveryExpansion(asset.id)}
                                  startIcon={isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                  sx={{
                                    alignSelf: 'flex-start',
                                    minWidth: 0,
                                    px: 0,
                                    py: 0,
                                    lineHeight: 1.1,
                                    fontSize: '0.7rem',
                                    textTransform: 'none',
                                  }}
                                >
                                  {isExpanded ? 'Show less' : `Show ${names.length - 2} more`}
                                </Button>
                              )}
                              {asnInfoLine}
                            </Box>
                          );
                        }

                        // DNS / URL → show resolved IPs stacked
                        const ipv4s = (d.resolved_ips ?? []).filter((r) => r.version === 'ipv4').map((r) => r.ip);
                        const ipv6s = (d.resolved_ips ?? []).filter((r) => r.version === 'ipv6').map((r) => r.ip);
                        const allIps = [...ipv4s, ...ipv6s];
                        if (!allIps.length) {
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <Typography {...dim}>unresolved</Typography>
                              {asnInfoLine}
                            </Box>
                          );
                        }
                        const isExpanded = !!expandedDiscoveries[asset.id];
                        const visibleIps = isExpanded ? allIps : allIps.slice(0, 3);
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {visibleIps.map((ip) => (
                              <Typography key={ip} variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {ip}
                              </Typography>
                            ))}
                            {allIps.length > 3 && (
                              <Button
                                size="small"
                                onClick={() => toggleDiscoveryExpansion(asset.id)}
                                startIcon={isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                sx={{
                                  alignSelf: 'flex-start',
                                  minWidth: 0,
                                  px: 0,
                                  py: 0,
                                  lineHeight: 1.1,
                                  fontSize: '0.7rem',
                                  textTransform: 'none',
                                }}
                              >
                                {isExpanded ? 'Show less' : `Show ${allIps.length - 3} more`}
                              </Button>
                            )}
                            {asnInfoLine}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={asset.discovery?.discovery_status || 'pending'}
                        color={discoveryChipColor(asset.discovery?.discovery_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatUiDateOnly(asset.created_at, timezone)}</TableCell>
                    {showActionsColumn && (
                      <TableCell
                        sx={{
                          position: 'sticky',
                          right: 0,
                          zIndex: 1,
                          bgcolor: 'background.paper',
                          boxShadow: '-4px 0 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, asset)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                    </TableRow>
                    {expandedAssetId === asset.id && (
                      <TableRow>
                        <TableCell
                          colSpan={showActionsColumn ? 9 : 8}
                          sx={{ py: 2, px: 2, bgcolor: 'action.hover', borderTop: 0 }}
                        >
                          {loadingDetailId === asset.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <CircularProgress size={20} />
                              <Typography variant="body2" color="text.secondary">
                                Loading…
                              </Typography>
                            </Box>
                          ) : (
                            (() => {
                              const detail = assetDetails[asset.id];
                              if (!detail) return null;
                              const executionStatusColor = (
                                s: string
                              ): 'success' | 'error' | 'warning' | 'info' | 'default' => {
                                if (s === 'completed') return 'success';
                                if (s === 'failed' || s === 'stopped') return 'error';
                                if (s === 'running') return 'info';
                                return 'default';
                              };
                              return (
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Attack types
                                    </Typography>
                                    {detail.attack_types.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">
                                        No executions for this asset.
                                      </Typography>
                                    ) : (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {detail.attack_types.map((at) => (
                                          <Chip
                                            key={at.id}
                                            label={at.name}
                                            size="small"
                                            variant="outlined"
                                          />
                                        ))}
                                      </Box>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Executions
                                    </Typography>
                                    {detail.executions.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">
                                        No executions for this asset.
                                      </Typography>
                                    ) : (
                                      <>
                                        <Box
                                          sx={{
                                            borderLeft: '2px solid',
                                            borderColor: 'divider',
                                            pl: 1.5,
                                            maxHeight: 220,
                                            overflowY: 'auto',
                                            '& > * + *': { mt: 1 },
                                          }}
                                        >
                                          {detail.executions.map((ex) => (
                                            <Box key={ex.id}>
                                              <Typography variant="caption" color="text.secondary">
                                                {ex.started_at
                                                  ? formatUiDateTime(ex.started_at, timezone)
                                                  : '—'}
                                              </Typography>
                                              <Typography variant="body2">
                                                {ex.scenario_name} · {ex.attack_type_name}
                                              </Typography>
                                              <Chip
                                                label={ex.status}
                                                size="small"
                                                color={executionStatusColor(ex.status)}
                                                sx={{ mt: 0.25 }}
                                              />
                                            </Box>
                                          ))}
                                        </Box>
                                        {detail.total_executions > 50 && (
                                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Showing latest 50 of {detail.total_executions} executions
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Related runbooks
                                    </Typography>
                                    {detail.runbooks.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">
                                        No runbooks reference this asset.
                                      </Typography>
                                    ) : (
                                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {detail.runbooks.map((rb) => (
                                          <li key={rb.id}>
                                            <Typography
                                              component={Link}
                                              to={`/runbooks?runbook=${rb.id}`}
                                              variant="body2"
                                              sx={{ color: 'primary.main' }}
                                            >
                                              {rb.name}
                                            </Typography>
                                          </li>
                                        ))}
                                      </Box>
                                    )}
                                  </Grid>
                                </Grid>
                              );
                            })()
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={addOpen} onClose={() => { setAddOpen(false); setNewValues(['']); setNewEntry({ value: '', reason: '' }); }} maxWidth="sm" fullWidth>
          <DialogTitle>Add Asset</DialogTitle>
          <DialogContent>
            {newValues.map((val, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                <TextField
                  autoFocus={index === 0}
                  fullWidth
                  margin="dense"
                  label={index === 0 ? 'Asset Value (IP, CIDR, DNS, URL)' : undefined}
                  placeholder="IP, CIDR, DNS, or URL"
                  value={val}
                  onChange={(e) => {
                    const updated = [...newValues];
                    updated[index] = e.target.value;
                    setNewValues(updated);
                  }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text');
                    const lines = text.split(/[\n\r]+/).map(l => l.trim()).filter(l => l);
                    if (lines.length > 1) {
                      e.preventDefault();
                      const updated = [...newValues];
                      updated.splice(index, 1, ...lines);
                      setNewValues(updated);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const updated = [...newValues];
                      updated.splice(index + 1, 0, '');
                      setNewValues(updated);
                      setTimeout(() => {
                        const inputs = document.querySelectorAll<HTMLInputElement>('[data-asset-input]');
                        if (inputs[index + 1]) inputs[index + 1].focus();
                      }, 50);
                    }
                  }}
                  inputProps={{ 'data-asset-input': true, maxLength: 255 }}
                />
                {newValues.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => setNewValues(newValues.filter((_, i) => i !== index))}
                    sx={{ mt: index === 0 ? 1 : 0, color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
                {index === newValues.length - 1 && (
                  <IconButton
                    size="small"
                    onClick={() => setNewValues([...newValues, ''])}
                    sx={{ mt: index === 0 ? 1 : 0, color: 'primary.main' }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <TextField
              fullWidth
              margin="dense"
              multiline
              rows={3}
              label="Reason (optional)"
              value={newEntry.reason || ''}
              onChange={(e) => setNewEntry({ ...newEntry, reason: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAddOpen(false); setNewValues(['']); setNewEntry({ value: '', reason: '' }); }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddAsset}
              disabled={addingAsset}
              startIcon={addingAsset ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {addingAsset
                ? `Adding ${newValues.filter(v => v.trim()).length > 1 ? 'assets...' : 'asset...'}`
                : 'Request Whitelist'}
            </Button>
          </DialogActions>
        </Dialog>

        {showActionsColumn && (
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {/* Research — disabled until feature is ready */}
            <MenuItem disabled>
              <ListItemIcon><ScienceIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Research</ListItemText>
            </MenuItem>

            {/* Create Scenario */}
            <MenuItem onClick={() => { navigate(`/operations?asset=${encodeURIComponent(menuAsset!.value)}`); handleMenuClose(); }}>
              <ListItemIcon><FlashOnIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Create Scenario</ListItemText>
            </MenuItem>

            {canEditDiscovery && <Divider />}

            {/* Re-run Discovery */}
            {canEditDiscovery && (
              <MenuItem onClick={() => { handleRefreshDiscovery(menuAsset!); handleMenuClose(); }}>
                <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Re-run Discovery</ListItemText>
              </MenuItem>
            )}

            {/* Edit Discovery */}
            {canEditDiscovery && (
              <MenuItem onClick={() => { handleEditDiscoveryOpen(menuAsset!); handleMenuClose(); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Edit Discovery</ListItemText>
              </MenuItem>
            )}

            {canEditDiscovery && <Divider />}

            {/* Delete */}
            {canEditDiscovery && (
              <MenuItem
                onClick={() => { handleDeleteOpen(menuAsset!); handleMenuClose(); }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            )}
          </Menu>
        )}

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Asset</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete{' '}
              <strong style={{ fontFamily: 'monospace' }}>{selectedAsset?.value}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will remove the asset and all its discovery data. This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Discovery Data</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              label="Hosting Provider"
              value={editPayload.hosting_provider || ''}
              onChange={(e) => setEditPayload({ ...editPayload, hosting_provider: e.target.value })}
              helperText="Example: aws, gcp, azure, on_prem"
            />
            <TextField
              fullWidth
              margin="dense"
              multiline
              rows={4}
              label="Notes"
              value={editPayload.notes || ''}
              onChange={(e) => setEditPayload({ ...editPayload, notes: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="contained" startIcon={<PlayCircleOutlineIcon />} onClick={handleSaveDiscovery}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PermissionGate>
  );
};

export default Assets;
